import { useParams, usePathname } from 'next/navigation';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { Tournament } from '@/api/cms/tournaments';
import { ButtonWithArray } from '@/components/buttons';
import { ColorMode } from '@/components/GameItem/styled';
import { IconComponent } from '@/components/IconComponent';
import { MediaType } from '@/helpers/enums';
import { GameStaticCategories } from '@/helpers/game';
import { oneRowSliderParams } from '@/helpers/games';
import useEstonia from '@/hooks/estonia';
import { useResponsive } from '@/modules/client';
import { Search } from '@/modules/lobby/components/FilterGames/Search';
import LobbyGamesSlider from '@/modules/lobby/components/LobbyGamesSlider';
import { useGetCommonStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { TournamentsWidget } from '../TournamentsWidget';
import {
  ScGamesCategoryModal,
  ScGamesCategoryModalBg,
  ScRecommendationsModal,
} from './styled';

type GameCateogry =
  | GameStaticCategories.Favorites
  | GameStaticCategories.Recent;

interface RecommendationsModalProps {
  gameTournaments: Tournament[];
}

export const RecommendationsModal = ({
  gameTournaments,
}: PropsWithChildren<RecommendationsModalProps>) => {
  const pathname = usePathname();
  const query = useParams();
  const { t } = useTranslation('common');
  const { isEstonia } = useEstonia();
  const useCommonStore = useGetCommonStore();

  const { favoriteGames, recentGames, isAuthenticated } = useCommonStore(
    useShallow((state) => ({
      favoriteGames: state.favoriteGames,
      recentGames: state.recentGames,
      isAuthenticated: state.isAuthenticated,
    }))
  );

  const ref = useRef<HTMLDivElement>(null);

  const [gamesCategoryModal, setGamesCategoryModal] =
    useState<GameCateogry | null>(null);

  const sliderGames = useMemo(() => {
    switch (gamesCategoryModal) {
      case GameStaticCategories.Favorites:
        return favoriteGames;
      case GameStaticCategories.Recent:
        return recentGames;
      default:
        return [];
    }
  }, [favoriteGames, gamesCategoryModal, recentGames]);

  const handleGamesCategoryClick = useCallback(
    (category: GameCateogry) =>
      gamesCategoryModal === category
        ? setGamesCategoryModal(null)
        : setGamesCategoryModal(category),
    [gamesCategoryModal]
  );

  const { currentMediaType } = useResponsive();

  const { cols } = useMemo(() => {
    switch (currentMediaType) {
      case MediaType.DESKTOP_L:
        return oneRowSliderParams.desktopL;
      case MediaType.TABLET:
        return oneRowSliderParams.tablet;
      case MediaType.MOBILE:
        return oneRowSliderParams.mobile;
      case MediaType.MOBILE_S:
        return oneRowSliderParams.mobileS;
      case MediaType.DESKTOP:
      default:
        return oneRowSliderParams.desktop;
    }
  }, [currentMediaType]);

  useEffect(() => {
    setGamesCategoryModal(null);
  }, [pathname, query]);

  const Slider = useCallback(
    () =>
      gamesCategoryModal ? (
        <LobbyGamesSlider
          blackShowAllCard
          categoryId={gamesCategoryModal}
          darkArrows
          gameColorMode={ColorMode.GRAY_BG}
          gamesList={sliderGames}
          noAllButton
          noArrows={sliderGames.length <= cols}
          plugBlack
          plugsOnEmpty
          sliderParams={oneRowSliderParams}
          title=""
        />
      ) : null,
    [cols, gamesCategoryModal, sliderGames]
  );

  return (
    <>
      <ScRecommendationsModal isEstonia={isEstonia}>
        <ScContainer>
          <div className="category-btns">
            <ButtonWithArray
              active={GameStaticCategories.Recent === gamesCategoryModal}
              icon={<IconComponent name="magnifier" />}
              onClick={() =>
                handleGamesCategoryClick(GameStaticCategories.Recent)
              }
            >
              {t('game.recentlyPlayed')}
            </ButtonWithArray>
            {isAuthenticated && (
              <ButtonWithArray
                active={GameStaticCategories.Favorites === gamesCategoryModal}
                icon={<IconComponent name="magnifier" />}
                onClick={() =>
                  handleGamesCategoryClick(GameStaticCategories.Favorites)
                }
              >
                {t('game.favorites')}
              </ButtonWithArray>
            )}
          </div>

          <TournamentsWidget gameTournaments={gameTournaments} />

          <Search />
        </ScContainer>
      </ScRecommendationsModal>

      {gamesCategoryModal && (
        <>
          <ScGamesCategoryModal isEstonia={isEstonia} ref={ref}>
            <ScContainer relative>
              <Slider />
            </ScContainer>
          </ScGamesCategoryModal>
          <ScGamesCategoryModalBg onClick={() => setGamesCategoryModal(null)} />
        </>
      )}
    </>
  );
};
